import {
  CheckboxVisibility,
  DetailsListLayoutMode,
  IColumn,
  IDropdownOption,
  IObjectWithKey,
  ISelection,
  MessageBarType,
  Selection,
  SelectionMode,
} from '@fluentui/react';
import {
  Button,
  buttonStylesDanger,
  buttonStylesGhost,
  ConfirmDialog,
  DetailsList,
  Dropdown,
  Item,
  MessageBar,
  Search,
  useTheme,
} from '@h2oai/ui-kit';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { GetCustomerManagedGroupsRequest } from '../../../../../admin-center/gen/custom_group_management/custom_group_management_pb';
import {
  GetMembersRequest,
  RemoveMemberRequest,
} from '../../../../../admin-center/gen/user_management/user_management_pb';
import { useAdminCenterService } from '../../../../../admin-center/hooks';
import { useUser } from '../../../../../utils/hooks';
import { messageBarContainerStyles } from '../../../common/common.styles';
import { buildUserContext, getDummyList } from '../../../common/utils';
import { OktaStatusIdpLabel, OktaStatusLabel, UMUserTableColumnKey, UMUserTableColumnName } from '../../../constants';
import SkeletonLoader from '../../SkeletonLoader/SkeletonLoader';
import StatsCard from '../../StatsCard/StatsCard';
import {
  actionContainerStyles,
  activeUserStyles,
  appleStyles,
  deactivatedUserStyles,
  githubStyles,
  googleStyles,
  lockedOutUserStyles,
  oktaStyles,
  passwordExpiredUserStyles,
  passwordResetUserStyles,
  pendingActionUserStyles,
  saml2Styles,
  searchStyle,
  stagedUserStyles,
  suspendedUserStyles,
  tableContainerStyles,
  unkownSourceStyles,
  unkownUserStatusStyles,
  userManagementTableStyles,
} from '../UserManagement.styles';

interface UserProfile {
  firstName: string | undefined;
  lastName: string | undefined;
  email: string | undefined;
  id: string | undefined;
  status: string | undefined;
  login: string | undefined;
  profileSource: string | undefined;
}

interface TableItem extends IObjectWithKey {
  userName: string;
  userEmail: string;
  userStatus: string;
  userSource: string;
}

interface CustomGroupsTableProps {
  onSubmitSuccess?: () => Promise<void>;
  refreshKey: number;
}

const dummyList = getDummyList<TableItem>(10);

const emptyMessageStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '40px',
  fontSize: '16px',
  color: '#666',
};

const dropdownStyle = {
  minWidth: '300px',
  maxWidth: '400px',
};

const rightActionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
};

export default function CustomGroupsTable({ onSubmitSuccess, refreshKey }: CustomGroupsTableProps) {
  const service = useAdminCenterService();
  const theme = useTheme();
  const user = useUser();
  const [customGroups, setCustomGroups] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | undefined>(undefined);
  const [members, setMembers] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGroupsLoading, setIsGroupsLoading] = useState(true);
  const [responseMessage, setResponseMessage] = useState<string | null>(null);
  const [tableSelections, setTableSelections] = useState<TableItem[]>([]);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState<boolean>(false);
  const [isDeletionPending, setIsDeletionPending] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showResponseMessage, setShowResponseMessage] = useState<boolean>(false);

  const fetchCustomGroups = async () => {
    setIsGroupsLoading(true);
    try {
      const req: GetCustomerManagedGroupsRequest = {};
      const response = await service.getCustomerManagedGroupsRequest(req);
      const groups = response.userGroups || [];
      setCustomGroups(groups);

      // Set first group as default if available
      if (groups.length > 0) {
        setSelectedGroup(groups[0]);
      }
    } catch (error) {
      console.error('Error fetching custom groups:', error);
    } finally {
      setIsGroupsLoading(false);
    }
  };

  const fetchMembers = async (groupName: string) => {
    setIsLoading(true);
    try {
      const req: GetMembersRequest = { groupName };
      const response = await service.getMembers(req);
      const membersData =
        response.members?.map((member) => ({
          firstName: member.firstName ?? 'Unknown',
          lastName: member.lastName ?? 'Unknown',
          email: member.email ?? 'Unknown',
          id: member.id ?? 'Unknown',
          status: member.status ?? 'Unknown',
          login: member.login ?? 'Unknown',
          profileSource: member.profileSource ?? 'Unknown',
        })) || [];
      setMembers(membersData);
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomGroups();
  }, [refreshKey]);

  useEffect(() => {
    if (selectedGroup) {
      fetchMembers(selectedGroup);
    }
  }, [selectedGroup, refreshKey]);

  const handleRefresh = async () => {
    if (selectedGroup) {
      await fetchMembers(selectedGroup);
    }
    if (onSubmitSuccess) {
      await onSubmitSuccess();
    }
  };

  const selectionRef = useRef<ISelection<IObjectWithKey>>(
    new Selection({
      onSelectionChanged: () => {
        const selectedItems = selectionRef.current.getSelection() as TableItem[];
        handleSelectionChanged(selectedItems);
      },
      getKey: (item) => (item as TableItem).userEmail,
    })
  );

  const handleSelectionChanged = useCallback((items: TableItem[]) => {
    setTableSelections(items);
  }, []);

  const memberData = members.map((member) => ({
    key: member.email || 'Unknown',
    userName: `${member.firstName || ''} ${member.lastName || ''}`,
    userStatus:
      (member.status?.endsWith('-IDP') ? OktaStatusIdpLabel[member.status] : OktaStatusLabel[member.status ?? '']) ||
      'Unknown',
    userEmail: member.email || 'Unknown',
    userSource: member.profileSource || 'Unknown',
  }));

  useEffect(() => {
    setTableSelections([]);
    if (selectionRef.current) {
      selectionRef.current.setAllSelected(false);
    }
  }, [selectedGroup]);

  const handleExport = () => {
    const exportData = tableSelections
      .map((member) => {
        return `Name: ${member.userName}\nEmail: ${member.userEmail}\nStatus: ${member.userStatus}\n`;
      })
      .join('\n');

    const blob = new Blob([exportData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `custom_group_export_${selectedGroup}_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDelete = async () => {
    if (!selectedGroup) return;

    setIsDeletionPending(true);
    const selectedUsers = tableSelections;
    const membersList = selectedUsers
      .filter((selectedUser) => user?.name !== selectedUser.userEmail)
      .map((selectedUser) => {
        const [firstName, ...lastNameParts] = selectedUser.userName.split(' ');
        return {
          firstName: firstName || '',
          lastName: lastNameParts.join(' ') || '',
          email: selectedUser.userEmail,
        };
      });

    if (membersList.length === 0) {
      setResponseMessage('⚠️ Cannot delete: You cannot remove yourself or no valid users selected');
      setShowDeleteConfirmationDialog(false);
      setShowResponseMessage(true);
      setIsDeletionPending(false);
      return;
    }

    try {
      const userContext = await buildUserContext(user);
      const request: RemoveMemberRequest = {
        members: membersList,
        groupNames: [selectedGroup],
        modifiedBy: user?.name ?? '',
        timestamp: Date.now() / 1000,
        userContext: userContext,
      };
      const response = await service.removeMembers(request);

      if (response.statusUpdate) {
        setResponseMessage(response.statusUpdate);
      } else {
        setResponseMessage('❌ Error: Failed to delete');
      }

      await handleRefresh();
    } catch (error) {
      setResponseMessage(`❌ Network error: ${error}`);
    } finally {
      setIsDeletionPending(false);
      setShowDeleteConfirmationDialog(false);
      setShowResponseMessage(true);
    }
  };

  const filteredData = useMemo(() => {
    if (!searchQuery) return memberData;
    const lowerQuery = searchQuery.toLowerCase();
    return memberData.filter(
      (item) =>
        item.userName.toLowerCase().includes(lowerQuery) ||
        item.userEmail.toLowerCase().includes(lowerQuery) ||
        item.userStatus.toLowerCase().includes(lowerQuery) ||
        item.userSource.toLowerCase().includes(lowerQuery)
    );
  }, [memberData, searchQuery]);

  const columns: IColumn[] = useMemo(
    () => [
      {
        key: UMUserTableColumnKey.name,
        name: UMUserTableColumnName.name,
        fieldName: 'userName',
        minWidth: 150,
        maxWidth: 200,
        isResizable: true,
        onRender: (item: TableItem) => <>{!isLoading ? item.userName : <SkeletonLoader width="65%" height="50%" />}</>,
      },
      {
        key: UMUserTableColumnKey.email,
        name: UMUserTableColumnName.email,
        fieldName: 'userEmail',
        minWidth: 200,
        maxWidth: 300,
        isResizable: true,
        onRender: (item: TableItem) => <>{!isLoading ? item.userEmail : <SkeletonLoader width="65%" height="50%" />}</>,
      },
      {
        key: UMUserTableColumnKey.status,
        name: UMUserTableColumnName.status,
        fieldName: 'userStatus',
        minWidth: 100,
        maxWidth: 150,
        isResizable: true,
        onRender: (item: TableItem) => {
          if (isLoading) return <SkeletonLoader width="65%" height="50%" />;

          let statusStyle = unkownUserStatusStyles;
          if (item.userStatus === OktaStatusLabel.ACTIVE) statusStyle = activeUserStyles;
          else if (item.userStatus === OktaStatusLabel.STAGED) statusStyle = stagedUserStyles;
          else if (item.userStatus === OktaStatusLabel.PROVISIONED) statusStyle = stagedUserStyles;
          else if (item.userStatus === OktaStatusLabel.DEPROVISIONED) statusStyle = deactivatedUserStyles;
          else if (item.userStatus === OktaStatusLabel.SUSPENDED) statusStyle = suspendedUserStyles;
          else if (item.userStatus === OktaStatusLabel.PASSWORD_EXPIRED) statusStyle = passwordExpiredUserStyles;
          else if (item.userStatus === OktaStatusLabel.LOCKED_OUT) statusStyle = lockedOutUserStyles;
          else if (item.userStatus === OktaStatusLabel.RECOVERY) statusStyle = passwordResetUserStyles;
          else if (item.userStatus === OktaStatusIdpLabel.ACTIVE_IDP) statusStyle = activeUserStyles;
          else if (item.userStatus === OktaStatusIdpLabel.STAGED_IDP) statusStyle = pendingActionUserStyles;

          return (
            <div>
              <Item
                data={{ id: '', title: item.userStatus }}
                styles={statusStyle(theme)}
                idField="id"
                labelField="title"
              />
            </div>
          );
        },
      },
      {
        key: UMUserTableColumnKey.source,
        name: UMUserTableColumnName.source,
        fieldName: 'userSource',
        minWidth: 100,
        maxWidth: 150,
        isResizable: true,
        onRender: (item: TableItem) => {
          if (isLoading) return <SkeletonLoader width="65%" height="50%" />;

          let sourceStyle = unkownSourceStyles;
          if (item.userSource === 'OKTA') sourceStyle = oktaStyles;
          else if (item.userSource === 'GOOGLE') sourceStyle = googleStyles;
          else if (item.userSource === 'GITHUB') sourceStyle = githubStyles;
          else if (item.userSource === 'APPLE') sourceStyle = appleStyles;
          else if (item.userSource === 'SAML2') sourceStyle = saml2Styles;

          return (
            <div>
              <Item
                data={{ id: '', title: item.userSource }}
                styles={sourceStyle(theme)}
                idField="id"
                labelField="title"
              />
            </div>
          );
        },
      },
    ],
    [theme, isLoading]
  );

  const groupOptions: IDropdownOption[] = customGroups.map((group) => ({
    key: group,
    text: group,
  }));

  if (customGroups.length === 0 && !isGroupsLoading) {
    return <div style={emptyMessageStyle}>You have no custom groups configured</div>;
  }

  return (
    <>
      {showResponseMessage && (
        <div style={messageBarContainerStyles}>
          <MessageBar
            timeout={4000}
            onDismiss={() => setShowResponseMessage(false)}
            messageBarType={
              responseMessage?.startsWith('❌') || responseMessage?.startsWith('⚠️')
                ? MessageBarType.error
                : MessageBarType.success
            }
          >
            {responseMessage}
          </MessageBar>
        </div>
      )}
      <StatsCard title="Total Members" stat={members.length} isLoading={isLoading} />
      <ConfirmDialog
        title="Remove Members"
        hidden={!showDeleteConfirmationDialog}
        onConfirm={handleDelete}
        onDismiss={() => setShowDeleteConfirmationDialog(false)}
        msg={
          isDeletionPending
            ? 'Deleting users...'
            : tableSelections.length === 1
            ? `Are you sure you want to remove the selected user from ${selectedGroup} group?`
            : `Are you sure you want to remove ${tableSelections.length} users from ${selectedGroup} group?`
        }
      >
        {isDeletionPending ? 'Processing...' : 'Are you sure you want to remove the selected members from this group?'}
      </ConfirmDialog>
      <div style={actionContainerStyles()}>
        <Search
          emptyMessage="No results found"
          placeholder="Search members..."
          onSearchTextChange={(text: string) => setSearchQuery(text)}
          style={searchStyle}
        />
        <div style={rightActionsStyle}>
          <Dropdown
            placeholder={isGroupsLoading ? 'Loading groups...' : 'Select a group'}
            options={groupOptions}
            selectedKey={selectedGroup}
            onChange={(_, option) => setSelectedGroup(option?.key as string)}
            disabled={isGroupsLoading}
            styles={{ root: dropdownStyle }}
          />
          <Button
            text="Export Members"
            styles={buttonStylesGhost}
            disabled={tableSelections.length === 0 || isLoading}
            onClick={handleExport}
          />
          <Button
            text="Remove"
            styles={buttonStylesDanger}
            onClick={() => setShowDeleteConfirmationDialog(true)}
            disabled={tableSelections.length === 0 || isLoading}
          />
        </div>
      </div>
      <div style={tableContainerStyles(theme)}>
        {!isLoading && members.length === 0 && selectedGroup ? (
          <MessageBar messageBarType={MessageBarType.warning}>This group is empty</MessageBar>
        ) : (
          <DetailsList
            items={isLoading ? dummyList : filteredData}
            columns={columns}
            setKey="userEmail"
            layoutMode={DetailsListLayoutMode.justified}
            selection={selectionRef.current}
            selectionMode={SelectionMode.multiple}
            checkboxVisibility={CheckboxVisibility.always}
            styles={userManagementTableStyles(theme)}
          />
        )}
      </div>
    </>
  );
}
